import { MediaProviders } from 'constants/media';
import {
  BirthdayVisibilityEnum,
  FileCategoryEnum,
  FileFormatEnum,
  type GetPlayerFinderProfileQuery,
  type GetPlayerFinderRatingScalesQuery,
  type PlayerFinderGoalsEnum,
  type SkillLevelValuesEnum,
  SportsEnum,
  type UserFilesInsertInput,
  type UserPadelRatingScalesInsertInput,
  type UserPickleballRatingScalesInsertInput,
  type UserPlayerFinderAvailabilityInsertInput,
  type UserTennisRatingScalesInsertInput,
  type UsersSetInput,
} from 'types/generated/client';
import { type AvailabilityDay, expandAvailabilityDay } from 'utils/shared/playerAvailability';
import { toVenueAddress } from 'screens/ListGame/utils';
import {
  OnboardingStep,
  type PlayingLevelValues,
  type PreferredCourtsValues,
  type ProfileBasicsValues,
  type ProfilePhotos,
  RatingSystem,
  type UploadedImage,
} from './types';

/**
 * Ids present before an edit but gone after it. Both the questionnaire and the
 * profile editors need this to deactivate only the rows the player actually
 * removed, rather than sweeping everything they didn't re-select.
 */
export const droppedIds = <T>(
  previous: T[],
  next: T[],
  getId: (item: T) => string | undefined,
): string[] => {
  const kept = new Set(next.map(getId).filter(Boolean));
  return previous.map(getId).filter((id): id is string => !!id && !kept.has(id));
};

/** Query-param key that persists the current onboarding step across reloads (e.g. ?step=verification). */
export const STEP_QUERY_KEY = 'step';

/** Parse the ?step= slug back to an OnboardingStep (case-insensitive), or null if unknown. */
export const parseStepSlug = (raw: string | string[] | undefined): OnboardingStep | null => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (!value) {
    return null;
  }
  const normalized = value.toLowerCase();
  return Object.values(OnboardingStep).find((step) => step.toLowerCase() === normalized) ?? null;
};

/**
 * Flattens the (day, time-bucket) selections into concrete rows, expanding the
 * WEEKENDS / "I'm flexible" shorthands and de-duping overlaps (e.g. Weekends +
 * an explicit Saturday). Only pairs with a chosen time bucket produce a row.
 */
export const buildAvailabilityRows = (
  values: PreferredCourtsValues,
  userId: string,
): UserPlayerFinderAvailabilityInsertInput[] => {
  const seen = new Set<string>();
  const rows: UserPlayerFinderAvailabilityInsertInput[] = [];

  values.days.forEach((day) => {
    const times = values.timesByDay[day] ?? [];
    expandAvailabilityDay(day).forEach((dayOfWeek) => {
      times.forEach((time) => {
        const key = `${dayOfWeek}|${time}`;
        if (seen.has(key)) return;
        seen.add(key);
        rows.push({
          userId,
          dayOfWeek,
          timeOfDay: time,
        });
      });
    });
  });

  return rows;
};

/** short_name → scale UUID, per sport (from GetPlayerFinderRatingScales). */
export interface RatingScaleIdMaps {
  pickleball: Record<string, string>;
  tennis: Record<string, string>;
  padel: Record<string, string>;
}

export interface RatingScaleInserts {
  pickleball: UserPickleballRatingScalesInsertInput[];
  tennis: UserTennisRatingScalesInsertInput[];
  padel: UserPadelRatingScalesInsertInput[];
}

/** A filled-in rating entry: system short_name + numeric value, dropping blanks. */
const enteredRatings = (systems: Partial<Record<RatingSystem, string>> | undefined) =>
  Object.entries(systems ?? {})
    .filter(([, value]) => value != null && value.trim() !== '')
    .map(([system, value]) => ({ system: system as RatingSystem, rating: Number(value) }))
    .filter((entry) => !Number.isNaN(entry.rating));

/**
 * Maps the form's per-sport structured ratings onto the three sport-specific
 * junction insert shapes, resolving each system's scale UUID. Systems whose scale
 * id can't be resolved are skipped (defensive against a stale scale map).
 */
export const buildRatingScaleInserts = (
  playingLevel: PlayingLevelValues,
  userId: string,
  maps: RatingScaleIdMaps,
): RatingScaleInserts => {
  const pickleball: UserPickleballRatingScalesInsertInput[] = [];
  const tennis: UserTennisRatingScalesInsertInput[] = [];
  const padel: UserPadelRatingScalesInsertInput[] = [];

  enteredRatings(playingLevel.ratings[SportsEnum.Pickleball]).forEach(({ system, rating }) => {
    const scaleId = maps.pickleball[system];
    if (scaleId) pickleball.push({ userId, pickleballRatingScaleId: scaleId, rating });
  });
  enteredRatings(playingLevel.ratings[SportsEnum.Tennis]).forEach(({ system, rating }) => {
    const scaleId = maps.tennis[system];
    if (scaleId) tennis.push({ userId, tennisRatingScaleId: scaleId, rating });
  });
  enteredRatings(playingLevel.ratings[SportsEnum.Padel]).forEach(({ system, rating }) => {
    const scaleId = maps.padel[system];
    if (scaleId) padel.push({ userId, padelRatingScaleId: scaleId, rating });
  });

  return { pickleball, tennis, padel };
};

/** Builds the short_name → scale-UUID maps (per sport) from the scales query. */
export const buildRatingScaleIdMaps = (
  data: GetPlayerFinderRatingScalesQuery | undefined,
): RatingScaleIdMaps => ({
  pickleball: Object.fromEntries(
    (data?.pickleballRatingScales ?? []).map((scale) => [scale.shortName, scale.id]),
  ),
  tennis: Object.fromEntries(
    (data?.tennisRatingScales ?? []).map((scale) => [scale.shortName, scale.id]),
  ),
  padel: Object.fromEntries(
    (data?.padelRatingScales ?? []).map((scale) => [scale.shortName, scale.id]),
  ),
});

/**
 * The `users` update for Profile basics, built non-destructively: a field is
 * included only when the player actually provided it, so an untouched or skipped
 * step never blanks out data set elsewhere (signup, profile edit, a VAIR sync).
 */
export const buildProfileBasicsSet = (
  values: ProfileBasicsValues,
  photos: ProfilePhotos,
): UsersSetInput => {
  const set: UsersSetInput = {};

  if (values.aboutMe.trim()) set.aboutMe = values.aboutMe;
  if (values.gender) set.gender = values.gender;
  if (values.birthday) {
    set.birthday = values.birthday;
    set.birthdayVisibility = values.birthdayVisibility;
  }

  // An empty social string means the toggle is off — leave that column untouched.
  const { socialLinks } = values;
  if (socialLinks.instagram) set.instagramUrl = socialLinks.instagram;
  if (socialLinks.facebook) set.facebookUrl = socialLinks.facebook;
  if (socialLinks.tiktok) set.tiktokUrl = socialLinks.tiktok;
  if (socialLinks.linkedin) set.linkedinUrl = socialLinks.linkedin;
  if (socialLinks.x) set.xUrl = socialLinks.x;

  // Profile photo (already uploaded) → the users.profile_image_* columns.
  const { profilePhoto } = photos;
  if (profilePhoto) {
    set.profileImageFileName = profilePhoto.fileName;
    set.profileImagePath = profilePhoto.path;
    set.profileImageProvider = profilePhoto.provider || MediaProviders.Cloudflare;
    set.profileImageProviderId = profilePhoto.fileName.split('.')[0];
    set.profileImageProviderUrl = profilePhoto.providerUrl;
    set.profileImageUrl = profilePhoto.url;
  }

  return set;
};

/** Additional photos → one `user_files` row each, tagged PLAYER_GALLERY. */
export const buildGalleryFileInserts = (
  photos: UploadedImage[],
  userId: string,
): UserFilesInsertInput[] =>
  photos
    .filter((photo) => !photo.id)
    .map((photo) => ({
      userId,
      fileCategory: FileCategoryEnum.PlayerGallery,
      // user_files.file_format is NOT NULL; derive it from the upload's mime type.
      fileFormat: photo.fileType.startsWith('video/') ? FileFormatEnum.Video : FileFormatEnum.Image,
      fileName: photo.fileName,
      originalFileName: photo.originalFileName,
      path: photo.path,
      provider: photo.provider || MediaProviders.Cloudflare,
      providerId: photo.fileName.split('.')[0],
      providerUrl: photo.providerUrl,
      url: photo.url,
      fileType: photo.fileType,
      // host is NOT NULL on user_files; fall back to '' like the coach upload path.
      host: photo.host ?? '',
    }));

/**
 * The `users` update for the self-rate skill levels — one column per sport, and
 * only for sports the player rated, so an untouched sport keeps its level.
 */
export const buildSkillLevelSet = (values: PlayingLevelValues): UsersSetInput => {
  const set: UsersSetInput = {};
  const pickleball = values.selfRatedSkillLevels[SportsEnum.Pickleball];
  const tennis = values.selfRatedSkillLevels[SportsEnum.Tennis];
  const padel = values.selfRatedSkillLevels[SportsEnum.Padel];
  if (pickleball) set.pickleballSkillLevelId = pickleball;
  if (tennis) set.tennisSkillLevelId = tennis;
  if (padel) set.padelSkillLevelId = padel;
  return set;
};

// ---------------------------------------------------------------------------
// Pre-fill: map the player's saved record back onto each step's form shape, so
// a returning player sees their answers instead of blank fields. Venues are not
// re-hydrated — see GET_PLAYER_FINDER_PROFILE for why.
// ---------------------------------------------------------------------------

/** Step 0 — the goals the player already picked. */
export const toSelectedGoals = (data: GetPlayerFinderProfileQuery): PlayerFinderGoalsEnum[] =>
  data.userPlayerFinderGoals.map((row) => row.goal);

/** Step 1 — profile text fields, gender, birthday and social links. */
export const toProfileBasicsValues = (data: GetPlayerFinderProfileQuery): ProfileBasicsValues => {
  const user = data.usersByPk;
  return {
    aboutMe: user?.aboutMe ?? '',
    gender: user?.gender ?? null,
    // Native date inputs want yyyy-MM-dd, which is how a date column serialises.
    birthday: user?.birthday ? String(user.birthday).slice(0, 10) : '',
    birthdayVisibility: user?.birthdayVisibility ?? BirthdayVisibilityEnum.Visible,
    socialLinks: {
      instagram: user?.instagramUrl ?? '',
      facebook: user?.facebookUrl ?? '',
      tiktok: user?.tiktokUrl ?? '',
      linkedin: user?.linkedinUrl ?? '',
      x: user?.xUrl ?? '',
    },
  };
};

/** Step 1 — the profile photo, rebuilt from the users.profile_image_* columns. */
export const toProfilePhotos = (data: GetPlayerFinderProfileQuery): ProfilePhotos => {
  const user = data.usersByPk;

  const additionalPhotos: UploadedImage[] = data.userFiles.map((file) => ({
    id: file.id,
    fileName: file.fileName ?? '',
    originalFileName: file.originalFileName ?? '',
    path: file.path ?? '',
    provider: file.provider ?? '',
    providerUrl: file.providerUrl ?? '',
    url: file.url ?? '',
    fileType: file.fileType ?? '',
    host: file.host ?? '',
  }));

  if (!user?.profileImageUrl) return { profilePhoto: null, additionalPhotos };

  const profilePhoto: UploadedImage = {
    fileName: user.profileImageFileName,
    // originalFileName / fileType aren't stored per profile image, and the save
    // path (buildProfileBasicsSet) doesn't read them, so empty is safe here.
    originalFileName: '',
    path: user.profileImagePath,
    provider: user.profileImageProvider,
    providerUrl: user.profileImageProviderUrl,
    url: user.profileImageUrl,
    fileType: '',
  };
  return { profilePhoto, additionalPhotos };
};

/** Step 2 — sports, self-rate skill levels and structured ratings. */
export const toPlayingLevelValues = (data: GetPlayerFinderProfileQuery): PlayingLevelValues => {
  const user = data.usersByPk;

  const selfRatedSkillLevels: PlayingLevelValues['selfRatedSkillLevels'] = {};
  if (user?.pickleballSkillLevelId)
    selfRatedSkillLevels[SportsEnum.Pickleball] =
      user.pickleballSkillLevelId as SkillLevelValuesEnum;
  if (user?.tennisSkillLevelId)
    selfRatedSkillLevels[SportsEnum.Tennis] = user.tennisSkillLevelId as SkillLevelValuesEnum;
  if (user?.padelSkillLevelId)
    selfRatedSkillLevels[SportsEnum.Padel] = user.padelSkillLevelId as SkillLevelValuesEnum;

  const ratings: PlayingLevelValues['ratings'] = {};
  const collect = (
    sport: SportsEnum,
    rows: { rating: unknown; scale: { shortName: string } }[],
  ) => {
    const bySystem: Partial<Record<RatingSystem, string>> = {};
    rows.forEach((row) => {
      bySystem[row.scale.shortName as RatingSystem] = String(row.rating);
    });
    if (Object.keys(bySystem).length) ratings[sport] = bySystem;
  };
  collect(SportsEnum.Pickleball, data.userPickleballRatingScales);
  collect(SportsEnum.Tennis, data.userTennisRatingScales);
  collect(SportsEnum.Padel, data.userPadelRatingScales);

  return { sports: data.userSports.map((row) => row.sport), ratings, selfRatedSkillLevels };
};

/** Step 3 — the player's active preferred courts plus their availability chips. */
export const toPreferredCourtsValues = (
  data: GetPlayerFinderProfileQuery,
): PreferredCourtsValues => {
  const days: AvailabilityDay[] = [];
  const timesByDay: PreferredCourtsValues['timesByDay'] = {};

  data.userPlayerFinderAvailability.forEach((row) => {
    const day = row.dayOfWeek as AvailabilityDay;
    const time = row.timeOfDay;
    const times = timesByDay[day];
    if (times) {
      times.push(time);
    } else {
      timesByDay[day] = [time];
      days.push(day);
    }
  });

  const venues = data.venueFollows
    .map((row) => row.venue)
    .filter((venue): venue is NonNullable<typeof venue> => !!venue)
    .map(toVenueAddress);

  return { venues, days, timesByDay };
};
